/**
 * Audit Logging System
 * SOC 2 Controls: CC6.1, CC7.2, CC7.3
 * ISO 27001: A.12.4.1, A.12.4.2, A.12.4.3
 */

import { supabase } from '@/integrations/supabase/client';

// =============================================================================
// TYPES
// =============================================================================

export type AuditAction =
  | 'login'
  | 'logout'
  | 'login_failed'
  | 'password_change'
  | 'mfa_enroll'
  | 'mfa_verify'
  | 'create'
  | 'read'
  | 'update'
  | 'delete'
  | 'export'
  | 'download'
  | 'upload'
  | 'permission_change'
  | 'role_assign'
  | 'role_revoke'
  | 'integration_connect'
  | 'integration_disconnect'
  | 'settings_change'
  | 'invite_sent'
  | 'invite_accepted';

export type AuditCategory =
  | 'authentication'
  | 'authorization'
  | 'data_access'
  | 'data_modification'
  | 'admin'
  | 'system';

export type AuditStatus = 'success' | 'failure' | 'denied';

export interface AuditLogEntry {
  org_id?: string;
  family_id?: string;
  action: AuditAction;
  action_category: AuditCategory;
  resource_type?: string;
  resource_id?: string;
  resource_name?: string;
  old_values?: Record<string, unknown>;
  new_values?: Record<string, unknown>;
  changes_summary?: string;
  status?: AuditStatus;
  failure_reason?: string;
  metadata?: Record<string, unknown>;
}

export interface AuditLogRecord extends AuditLogEntry {
  id: string;
  user_id: string;
  user_email: string;
  user_role: string;
  ip_address: string;
  user_agent: string;
  request_id: string;
  session_id: string;
  created_at: string;
}

// =============================================================================
// SENSITIVE FIELD FILTERING
// =============================================================================

const SENSITIVE_FIELDS = [
  'password',
  'password_hash',
  'access_token',
  'refresh_token',
  'credentials_token',
  'api_key',
  'secret',
  'ssn',
  'tax_id',
  'account_number',
  'routing_number',
  'credit_card',
  'cvv',
];

/**
 * Remove sensitive fields from an object before logging
 */
export function sanitizeForAudit<T extends Record<string, unknown>>(obj: T | null | undefined): T | null {
  if (!obj) return null;

  const sanitized = { ...obj };
  for (const field of SENSITIVE_FIELDS) {
    if (field in sanitized) {
      sanitized[field] = '[REDACTED]';
    }
  }
  return sanitized;
}

/**
 * Compute a human-readable summary of changes between two objects
 */
export function computeChangesSummary(
  oldValues: Record<string, unknown> | null,
  newValues: Record<string, unknown> | null
): string {
  if (!oldValues && newValues) {
    return 'Created new record';
  }
  if (oldValues && !newValues) {
    return 'Deleted record';
  }
  if (!oldValues || !newValues) {
    return '';
  }

  const changes: string[] = [];
  const allKeys = new Set([...Object.keys(oldValues), ...Object.keys(newValues)]);

  for (const key of allKeys) {
    if (SENSITIVE_FIELDS.includes(key)) {
      if (oldValues[key] !== newValues[key]) {
        changes.push(`${key} was changed`);
      }
    } else if (JSON.stringify(oldValues[key]) !== JSON.stringify(newValues[key])) {
      changes.push(`${key}: ${oldValues[key]} → ${newValues[key]}`);
    }
  }

  return changes.length > 0 ? changes.join(', ') : 'No changes detected';
}

// =============================================================================
// AUDIT LOGGER CLASS
// =============================================================================

class AuditLogger {
  private requestId: string;
  private sessionId: string | null = null;

  constructor() {
    // Generate a unique request ID for this page session
    this.requestId = crypto.randomUUID();
    // Try to get session ID from Supabase
    this.initSession();
  }

  private async initSession() {
    const { data } = await supabase.auth.getSession();
    this.sessionId = data.session?.access_token?.slice(-16) || null;
  }

  /**
   * Write an audit log entry
   */
  async log(entry: AuditLogEntry): Promise<string | null> {
    try {
      // Get current org context
      const orgId = entry.org_id || this.getCurrentOrgId();

      const { data, error } = await supabase.from('audit_logs').insert({
        org_id: orgId,
        family_id: entry.family_id,
        action: entry.action,
        action_category: entry.action_category,
        resource_type: entry.resource_type,
        resource_id: entry.resource_id,
        resource_name: entry.resource_name,
        old_values: sanitizeForAudit(entry.old_values),
        new_values: sanitizeForAudit(entry.new_values),
        changes_summary: entry.changes_summary || computeChangesSummary(
          entry.old_values || null,
          entry.new_values || null
        ),
        status: entry.status || 'success',
        failure_reason: entry.failure_reason,
        metadata: entry.metadata || {},
        user_agent: navigator.userAgent,
        request_id: this.requestId,
        session_id: this.sessionId,
      }).select('id').single();

      if (error) {
        console.error('Failed to write audit log:', error);
        return null;
      }

      return data?.id || null;
    } catch (err) {
      console.error('Audit logging error:', err);
      return null;
    }
  }

  /**
   * Get current org ID from local storage or URL
   */
  private getCurrentOrgId(): string | undefined {
    // Try to get from localStorage
    const stored = localStorage.getItem('currentOrgId');
    if (stored) return stored;

    // Could also parse from URL if needed
    return undefined;
  }

  // ==========================================================================
  // CONVENIENCE METHODS
  // ==========================================================================

  /**
   * Log authentication events
   */
  async logAuth(
    action: 'login' | 'logout' | 'login_failed' | 'password_change' | 'mfa_enroll' | 'mfa_verify',
    options: {
      status?: AuditStatus;
      failureReason?: string;
      metadata?: Record<string, unknown>;
    } = {}
  ): Promise<void> {
    await this.log({
      action,
      action_category: 'authentication',
      status: options.status || 'success',
      failure_reason: options.failureReason,
      metadata: options.metadata,
    });
  }

  /**
   * Log data access (read/export/download)
   */
  async logDataAccess(
    action: 'read' | 'export' | 'download',
    resourceType: string,
    resourceId: string,
    resourceName?: string,
    metadata?: Record<string, unknown>
  ): Promise<void> {
    await this.log({
      action,
      action_category: 'data_access',
      resource_type: resourceType,
      resource_id: resourceId,
      resource_name: resourceName,
      metadata,
    });
  }

  /**
   * Log data modifications (create/update/delete)
   */
  async logDataChange(
    action: 'create' | 'update' | 'delete',
    resourceType: string,
    resourceId: string,
    options: {
      resourceName?: string;
      oldValues?: Record<string, unknown>;
      newValues?: Record<string, unknown>;
      metadata?: Record<string, unknown>;
    } = {}
  ): Promise<void> {
    await this.log({
      action,
      action_category: 'data_modification',
      resource_type: resourceType,
      resource_id: resourceId,
      resource_name: options.resourceName,
      old_values: options.oldValues,
      new_values: options.newValues,
      metadata: options.metadata,
    });
  }

  /**
   * Log permission/role changes
   */
  async logPermissionChange(
    action: 'role_assign' | 'role_revoke' | 'permission_change',
    targetUserId: string,
    targetUserEmail: string,
    options: {
      oldRole?: string;
      newRole?: string;
      scope?: 'org' | 'family';
      familyId?: string;
      metadata?: Record<string, unknown>;
    } = {}
  ): Promise<void> {
    await this.log({
      action,
      action_category: 'authorization',
      resource_type: options.scope === 'family' ? 'users_family_roles' : 'users_org_roles',
      resource_id: targetUserId,
      resource_name: targetUserEmail,
      old_values: options.oldRole ? { role: options.oldRole } : undefined,
      new_values: options.newRole ? { role: options.newRole } : undefined,
      family_id: options.familyId,
      metadata: options.metadata,
    });
  }

  /**
   * Log integration events
   */
  async logIntegration(
    action: 'integration_connect' | 'integration_disconnect',
    integrationType: string,
    integrationId: string,
    integrationName: string,
    status: AuditStatus = 'success',
    failureReason?: string
  ): Promise<void> {
    await this.log({
      action,
      action_category: 'admin',
      resource_type: integrationType,
      resource_id: integrationId,
      resource_name: integrationName,
      status,
      failure_reason: failureReason,
    });
  }

  /**
   * Log admin/settings changes
   */
  async logAdminAction(
    action: 'settings_change' | 'invite_sent' | 'invite_accepted',
    resourceType: string,
    resourceId: string,
    options: {
      resourceName?: string;
      oldValues?: Record<string, unknown>;
      newValues?: Record<string, unknown>;
      metadata?: Record<string, unknown>;
    } = {}
  ): Promise<void> {
    await this.log({
      action,
      action_category: 'admin',
      resource_type: resourceType,
      resource_id: resourceId,
      resource_name: options.resourceName,
      old_values: options.oldValues,
      new_values: options.newValues,
      metadata: options.metadata,
    });
  }

  /**
   * Log access denied events
   */
  async logAccessDenied(
    resourceType: string,
    resourceId: string,
    reason: string,
    metadata?: Record<string, unknown>
  ): Promise<void> {
    await this.log({
      action: 'read',
      action_category: 'data_access',
      resource_type: resourceType,
      resource_id: resourceId,
      status: 'denied',
      failure_reason: reason,
      metadata,
    });
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

export const auditLog = new AuditLogger();

// =============================================================================
// QUERY FUNCTIONS FOR AUDIT LOG VIEWER
// =============================================================================

export interface AuditLogQuery {
  orgId: string;
  startDate?: Date;
  endDate?: Date;
  userId?: string;
  action?: AuditAction;
  actionCategory?: AuditCategory;
  resourceType?: string;
  status?: AuditStatus;
  limit?: number;
  offset?: number;
}

export async function queryAuditLogs(query: AuditLogQuery): Promise<{
  data: AuditLogRecord[];
  count: number;
  error: Error | null;
}> {
  try {
    let queryBuilder = supabase
      .from('audit_logs')
      .select('*', { count: 'exact' })
      .eq('org_id', query.orgId)
      .order('created_at', { ascending: false });

    if (query.startDate) {
      queryBuilder = queryBuilder.gte('created_at', query.startDate.toISOString());
    }
    if (query.endDate) {
      queryBuilder = queryBuilder.lte('created_at', query.endDate.toISOString());
    }
    if (query.userId) {
      queryBuilder = queryBuilder.eq('user_id', query.userId);
    }
    if (query.action) {
      queryBuilder = queryBuilder.eq('action', query.action);
    }
    if (query.actionCategory) {
      queryBuilder = queryBuilder.eq('action_category', query.actionCategory);
    }
    if (query.resourceType) {
      queryBuilder = queryBuilder.eq('resource_type', query.resourceType);
    }
    if (query.status) {
      queryBuilder = queryBuilder.eq('status', query.status);
    }

    queryBuilder = queryBuilder
      .limit(query.limit || 50)
      .range(query.offset || 0, (query.offset || 0) + (query.limit || 50) - 1);

    const { data, count, error } = await queryBuilder;

    if (error) throw error;

    return {
      data: (data as AuditLogRecord[]) || [],
      count: count || 0,
      error: null,
    };
  } catch (err) {
    return {
      data: [],
      count: 0,
      error: err as Error,
    };
  }
}

/**
 * Get audit log statistics for compliance dashboard
 */
export async function getAuditStats(orgId: string, days: number = 30): Promise<{
  totalEvents: number;
  authEvents: number;
  dataAccessEvents: number;
  failedEvents: number;
  deniedEvents: number;
  uniqueUsers: number;
}> {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  const { data, error } = await supabase
    .from('audit_logs')
    .select('action_category, status, user_id')
    .eq('org_id', orgId)
    .gte('created_at', startDate.toISOString());

  if (error || !data) {
    return {
      totalEvents: 0,
      authEvents: 0,
      dataAccessEvents: 0,
      failedEvents: 0,
      deniedEvents: 0,
      uniqueUsers: 0,
    };
  }

  const uniqueUsers = new Set(data.map((d) => d.user_id).filter(Boolean));

  return {
    totalEvents: data.length,
    authEvents: data.filter((d) => d.action_category === 'authentication').length,
    dataAccessEvents: data.filter((d) => d.action_category === 'data_access').length,
    failedEvents: data.filter((d) => d.status === 'failure').length,
    deniedEvents: data.filter((d) => d.status === 'denied').length,
    uniqueUsers: uniqueUsers.size,
  };
}
